/**
 * Pure, dependency-free helpers for the navigation "journey" (breadcrumb trail).
 *
 * A journey is an ordered array of ancestor entries that the user actually walked
 * through to reach the current page, e.g. Projects → Project ABC → Campaign XYZ.
 * It is carried in React Router's history `state` (see hooks/useJourney), so it is
 * scoped to the browser tab/session, survives refresh and Back/Forward (each
 * history entry owns its own frozen snapshot), and is empty on direct URL entry.
 *
 * These functions are intentionally free of React / react-router so they are
 * trivially unit-testable and can never mutate a previous history entry.
 *
 * An entry is: { url, label, key }
 *   url   : full "pathname?search" of that ancestor at navigation time. Returning
 *           to this URL restores the ancestor's list-state (useListState) and
 *           scroll (useScrollRestore) for free — the journey never re-implements
 *           that; it only remembers the opaque URL.
 *   label : display text for the breadcrumb node.
 *   key   : page identity used for de-dup / loop detection = the PATHNAME only.
 *           Search/query is deliberately excluded so a filter/search change on the
 *           same page is never treated as a new journey node.
 */

/** Identity of a page: the pathname, ignoring the query string. */
export function keyForUrl(url) {
  if (!url) return '';
  const q = url.indexOf('?');
  const hash = url.indexOf('#');
  let end = url.length;
  if (q !== -1) end = Math.min(end, q);
  if (hash !== -1) end = Math.min(end, hash);
  return url.slice(0, end);
}

/** Coerce a loose entry into a valid, complete entry (or null if unusable). */
export function normalizeEntry(entry) {
  if (!entry || !entry.url) return null;
  return {
    url: entry.url,
    label: entry.label == null ? '' : String(entry.label),
    key: entry.key || keyForUrl(entry.url),
  };
}

function sanitizeTrail(journey) {
  return Array.isArray(journey) ? journey.map(normalizeEntry).filter(Boolean) : [];
}

/**
 * Compute the journey to attach to a destination navigation.
 *
 * @param {Array}  journey incoming trail = ancestors of the CURRENT page.
 * @param {Object} self    entry describing the CURRENT page being left
 *                         { url, label, key? }.
 * @param {string} destUrl destination URL being navigated to.
 * @returns {Array} the new trail for the destination page.
 *
 * Guarantees (navigation-safety):
 *  - No duplicate consecutive entries.
 *  - No A → B → A loops: navigating to a page already in the trail collapses the
 *    trail back to that page (its own ancestors), rather than growing it.
 *  - Same-page navigation (query/filter change, or re-nav to self) adds no node.
 *  - Never mutates the input arrays.
 */
export function buildJourney(journey, self, destUrl) {
  const trail = sanitizeTrail(journey);
  const destKey = keyForUrl(destUrl);
  const selfEntry = normalizeEntry(self);

  // Loop / back-to-ancestor: the destination is already an ancestor. Collapse to
  // everything BEFORE it — those become the destination's ancestors.
  const destIdx = trail.findIndex((e) => e.key === destKey);
  if (destIdx !== -1) return trail.slice(0, destIdx);

  if (!selfEntry) return trail;

  // Same page (query/filter change or re-navigation to self) → not a new node.
  if (selfEntry.key === destKey) return trail;

  // No duplicate consecutive entry — refresh the stored url/label, don't grow.
  const last = trail[trail.length - 1];
  if (last && last.key === selfEntry.key) {
    return [...trail.slice(0, -1), selfEntry];
  }

  // Cycle: the current page is already somewhere earlier in the trail — collapse.
  const selfIdx = trail.findIndex((e) => e.key === selfEntry.key);
  if (selfIdx !== -1) {
    return [...trail.slice(0, selfIdx), selfEntry];
  }

  return [...trail, selfEntry];
}

/**
 * Trail to attach when jumping to ancestor at index `i` (a breadcrumb click):
 * that ancestor's own ancestors are everything before it.
 */
export function trailForAncestor(journey, i) {
  const trail = sanitizeTrail(journey);
  return trail.slice(0, Math.max(0, i));
}

/**
 * Decide how a journey-aware "Back" should behave. Kept pure so the decision is
 * unit-testable; the hook merely executes the result.
 *
 * @param {Array}   journey    the current page's incoming trail.
 * @param {boolean} hasHistory whether a real in-app history entry precedes this
 *                             one (react-router `location.key !== 'default'`).
 * @param {string}  fallback   route to use on direct entry with no trail.
 * @returns one of:
 *   { type: 'history' }                       → navigate(-1): clean browser pop
 *   { type: 'url', url, journey }             → navigate(url) with truncated trail
 *   { type: 'fallback', url }                 → navigate(fallback)
 */
export function resolveBack({ journey, hasHistory, fallback = '/' } = {}) {
  const trail = sanitizeTrail(journey);
  if (trail.length === 0) return { type: 'fallback', url: fallback };
  // Prefer a real browser pop when we have history: it restores the previous
  // URL + its journey snapshot + scroll exactly, matching existing smart-back and
  // avoiding history bloat / Back-vs-Forward fights.
  if (hasHistory) return { type: 'history' };
  // Arrived with a trail snapshot but no poppable history (e.g. forward-nav to a
  // deep link that carried state): go straight to the last ancestor's URL.
  const target = trail[trail.length - 1];
  return { type: 'url', url: target.url, journey: trail.slice(0, -1) };
}

/**
 * Split a trail for compact rendering when it is too long. Keeps the first node,
 * collapses the middle behind an overflow control, and keeps the last `tailCount`
 * ancestors. No node is ever lost — the middle stays accessible via `overflow`.
 *
 * @returns { head, overflow, tail } where head is Entry|null, overflow/tail are
 *          arrays of { entry, index } preserving original trail indexes.
 */
export function compactTrail(journey, { maxVisible = 4, tailCount = 2 } = {}) {
  const trail = sanitizeTrail(journey);
  const indexed = trail.map((entry, index) => ({ entry, index }));
  if (indexed.length <= maxVisible) {
    return { head: null, overflow: [], tail: indexed };
  }
  const head = indexed[0];
  const tail = indexed.slice(indexed.length - tailCount);
  const overflow = indexed.slice(1, indexed.length - tailCount);
  return { head, overflow, tail };
}
